/* ============================================================
   DIETMONSTA · Custom Prep plate builder

   Pick a goal, build a plate, add every item as its own cart line.
   Minimums are enforced here for UX only — see PROJECT.md for why
   server-side enforcement needs a public App Store app on the Grow plan.
   ============================================================ */

class DMCustomPrep extends HTMLElement {
  connectedCallback() {
    this.strings = this.#readStrings();
    this.minProtein = parseInt(this.dataset.minProtein, 10) || 0;
    this.minAddon = parseInt(this.dataset.minAddon, 10) || 0;
    this.packSizes = (this.dataset.packSizes || '10,20')
      .split(',')
      .map((n) => parseInt(n, 10))
      .filter((n) => n > 0);
    this.packSize = this.packSizes[0] || 10;

    this.goal = null; // { tag, label, carbLabel, vegLabel, hasVeg }
    this.selection = new Map(); // variantId -> qty

    this.rows = Array.from(this.querySelectorAll('[data-dm-item]'));

    const all = (sel) => Array.from(this.querySelectorAll(sel));
    this.els = {
      total: all('[data-dm-total]'),
      submit: all('[data-dm-submit]'),
      error: all('[data-dm-error]'),
      goalLabel: all('[data-dm-goal-label]'),
      carbLabel: all('[data-dm-carb-label]'),
      vegLabel: all('[data-dm-veg-label]'),
      sizeNote: all('[data-dm-size-note]'),
      hint: all('[data-dm-hint]'),
      proteinFilled: all('[data-dm-protein-filled]'),
      proteinTarget: all('[data-dm-protein-target]'),
      proteinProgress: all('[data-dm-protein-progress]'),
      carbFilled: all('[data-dm-carb-filled]'),
      carbTarget: all('[data-dm-carb-target]'),
      carbProgress: all('[data-dm-carb-progress]'),
      carbGroup: this.querySelector('[data-dm-carb-group]'),
      carbGrid: this.querySelector('[data-dm-carb-grid]'),
      vegFilled: all('[data-dm-veg-filled]'),
      vegTarget: all('[data-dm-veg-target]'),
      vegProgress: all('[data-dm-veg-progress]'),
      // Two different roles: the main grid section (also the is-next
      // outline target) and the sidebar summary row — both hidden
      // together for a goal with no separate vegetables step, but only
      // the first should ever get the outline.
      vegGroup: this.querySelector('[data-dm-veg-group]'),
      vegGrid: this.querySelector('[data-dm-veg-grid]'),
      vegHideable: all('[data-dm-veg-group], [data-dm-veg-row]'),
      list: this.querySelector('[data-dm-list]'),
      empty: this.querySelector('[data-dm-empty]'),
      live: this.querySelector('[data-dm-live]'),
    };

    this.#bind();
    this.#render();
  }

  #readStrings() {
    const node = this.querySelector('[data-dm-strings]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent);
    } catch {
      return {};
    }
  }

  #bind() {
    this.addEventListener('click', (event) => {
      const goalBtn = event.target.closest('[data-dm-pick-goal]');
      if (goalBtn) {
        return this.#pickGoal({
          tag: (goalBtn.dataset.dmPickGoal || '').toLowerCase(),
          label: goalBtn.dataset.goalLabel || '',
          carbLabel: goalBtn.dataset.carbLabel || '',
          vegLabel: goalBtn.dataset.vegLabel || '',
          hasVeg: goalBtn.dataset.hasVeg !== 'false',
          sizeNote: goalBtn.dataset.sizeNote || '',
        });
      }

      const sizeBtn = event.target.closest('[data-dm-pick-size]');
      if (sizeBtn) return this.#pickPackSize(parseInt(sizeBtn.dataset.dmPickSize, 10));

      if (event.target.closest('[data-dm-change-goal]')) {
        this.dataset.step = 'goal';
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }

      if (event.target.closest('[data-dm-change-size]')) {
        this.dataset.step = 'size';
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }

      const row = event.target.closest('[data-dm-item]');
      if (row) {
        if (event.target.closest('[data-dm-add], [data-dm-inc]')) return this.#bump(row, +1);
        if (event.target.closest('[data-dm-dec]')) return this.#bump(row, -1);
      }

      if (event.target.closest('[data-dm-submit]')) return this.#submit();
    });
  }

  #pickGoal(goal) {
    const changed = !this.goal || this.goal.tag !== goal.tag;
    this.goal = goal;

    // Vegetable rows always start back in their own grid, then get
    // folded into the Carbs grid below for a goal with no separate
    // vegetables step. Resetting first (rather than only moving on
    // change) keeps this correct regardless of which goal was picked
    // last.
    if (this.els.vegGrid) {
      this.rows.forEach((row) => {
        if (row.hasAttribute('data-dm-veg') && row.parentElement !== this.els.vegGrid) {
          this.els.vegGrid.appendChild(row);
        }
      });
    }

    // Show only this goal's carb/veg add-ons.
    this.rows.forEach((row) => {
      if (!this.#isAddonRow(row)) return;
      const tags = (row.dataset.tags || '').split(',').map((t) => t.trim());
      const matches = tags.includes(goal.tag);
      row.hidden = !matches;

      // Switching goals must drop the previous goal's add-ons. Without this
      // they stay in the selection while hidden, and the shopper is charged
      // for items they can no longer see or remove.
      if (changed && !matches) this.selection.delete(row.dataset.variantId);

      if (matches && !goal.hasVeg && row.hasAttribute('data-dm-veg') && this.els.carbGrid) {
        this.els.carbGrid.appendChild(row);
      }
    });

    this.els.vegHideable.forEach((el) => {
      el.hidden = !goal.hasVeg;
    });

    this.els.goalLabel.forEach((el) => {
      el.textContent = goal.label;
    });
    this.els.carbLabel.forEach((el) => {
      el.textContent = goal.carbLabel;
    });
    this.els.vegLabel.forEach((el) => {
      el.textContent = goal.vegLabel;
    });
    this.els.sizeNote.forEach((el) => {
      el.textContent = goal.sizeNote;
    });

    this.dataset.step = 'size';
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    this.#render();
  }

  #pickPackSize(size) {
    if (!Number.isFinite(size) || size <= 0) return;
    this.packSize = size;
    // #render() sets the target text for both groups — no need to set it
    // here too.
    this.dataset.step = 'build';
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    this.#render();
  }

  /* Three independent groups now: protein, carbs (rice), and vegetables.
     Each is its own full target, not a shared one — see #counts(). */
  #isAddonRow(row) {
    return row.hasAttribute('data-dm-carb') || row.hasAttribute('data-dm-veg');
  }

  #groupOf(row) {
    if (row.hasAttribute('data-dm-carb')) return 'carb';
    if (row.hasAttribute('data-dm-veg')) {
      // A goal with no separate vegetables step folds its veg products
      // into the carbs count instead of tracking a third target.
      return this.goal && this.goal.hasVeg === false ? 'carb' : 'veg';
    }
    return 'protein';
  }

  /* Add-ons are sold as a pack ("150g x 5"), so one click on the row adds
     a whole pack's worth of servings, not one — see #counts(). Protein
     packs are single 120g servings, so this is always 1 there. */
  #packQty(row) {
    if (!this.#isAddonRow(row)) return 1;
    const n = parseInt(row.dataset.packQty, 10);
    return Number.isFinite(n) && n > 0 ? n : 1;
  }

  #bump(row, delta) {
    const id = row.dataset.variantId;
    const next = (this.selection.get(id) || 0) + delta;
    if (next < 0) return;
    // The chosen pack size is a per-group cap, not a shared total — a
    // customer picking "10" can take up to 10 protein packs AND up to 10
    // carbs AND up to 10 vegetables, not 10 shared across all three.
    if (delta > 0) {
      const group = this.#groupOf(row);
      const counts = this.#counts();
      const groupCount = counts[group];
      // One click adds this row's whole pack quantity (5, for a "pack of
      // 5" add-on), so the check has to be "would this click overshoot",
      // not "have we already reached the cap" — otherwise a click that
      // lands exactly on target would be wrongly blocked one click early.
      if (groupCount + this.#packQty(row) > this.packSize) return;
    }
    if (next === 0) this.selection.delete(id);
    else this.selection.set(id, next);
    this.#render();
  }

  #rowFor(id) {
    return this.rows.find((r) => r.dataset.variantId === id);
  }

  /* Counts split by group, since each group's target is independent.
     Weighted by each row's pack quantity so the totals (and the "X / 10"
     progress they drive) reflect real servings, not raw click counts —
     clicking a "pack of 5" add-on twice reads as 10, not 2. */
  #counts() {
    let protein = 0;
    let carb = 0;
    let veg = 0;
    this.selection.forEach((qty, id) => {
      const row = this.#rowFor(id);
      if (!row) return;
      const servings = qty * this.#packQty(row);
      const group = this.#groupOf(row);
      if (group === 'carb') carb += servings;
      else if (group === 'veg') veg += servings;
      else protein += servings;
    });
    return { protein, carb, veg, total: protein + carb + veg };
  }

  #subtotal() {
    let cents = 0;
    this.selection.forEach((qty, id) => {
      const row = this.#rowFor(id);
      if (row) cents += parseInt(row.dataset.price, 10) * qty;
    });
    return cents;
  }

  #money(cents) {
    const currency = this.strings.currency || 'MYR';
    const value = cents / 100;
    try {
      const parts = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
      }).formatToParts(value);
      // Matches the card price display: whole amounts drop the ".00",
      // fractional ones keep their cents.
      const fraction = parts.find((p) => p.type === 'fraction');
      const dropDecimals = fraction && /^0+$/.test(fraction.value);
      return {
        symbol: parts.filter((p) => p.type === 'currency').map((p) => p.value).join(''),
        amount: parts
          .filter((p) => p.type !== 'currency' && p.type !== 'literal')
          .filter((p) => !dropDecimals || (p.type !== 'decimal' && p.type !== 'fraction'))
          .map((p) => p.value)
          .join(''),
      };
    } catch {
      return { symbol: '', amount: Number.isInteger(value) ? String(value) : value.toFixed(2) };
    }
  }

  #render() {
    const { protein, carb, veg, total } = this.#counts();
    // Each group must be filled to exactly packSize — picking "10" means
    // 10 protein AND 10 carbs AND 10 vegetables, not 10 shared between them.
    const proteinDone = protein >= this.packSize;
    const carbDone = carb >= this.packSize;
    // A goal with no separate vegetables step has nothing tracked as
    // 'veg' (see #groupOf), so treat it as already satisfied rather than
    // a target that can never be reached.
    const hasVeg = !this.goal || this.goal.hasVeg !== false;
    const vegDone = hasVeg ? veg >= this.packSize : true;
    const groupDone = { protein: proteinDone, carb: carbDone, veg: vegDone };

    this.rows.forEach((row) => {
      const qty = this.selection.get(row.dataset.variantId) || 0;
      const addBtn = row.querySelector('[data-dm-add]');
      const stepper = row.querySelector('[data-dm-stepper]');
      const badge = row.querySelector('[data-dm-qty-badge]');

      row.classList.toggle('is-selected', qty > 0);

      if (addBtn && stepper) {
        addBtn.hidden = qty > 0;
        stepper.hidden = qty === 0;
      }
      if (badge) {
        badge.hidden = qty === 0;
        const bv = row.querySelector('[data-dm-qty-badge-val]');
        if (bv) bv.textContent = String(qty);
      }
      const qtyEl = row.querySelector('[data-dm-qty]');
      if (qtyEl) qtyEl.textContent = String(qty);

      // Block further additions once THIS group hits its own target.
      const done = groupDone[this.#groupOf(row)];
      if (addBtn) addBtn.disabled = done;
      const incBtn = row.querySelector('[data-dm-inc]');
      if (incBtn) incBtn.disabled = done;
    });

    // --- per-group progress (protein) ---
    this.els.proteinFilled.forEach((el) => {
      el.textContent = String(protein);
    });
    this.els.proteinTarget.forEach((el) => {
      el.textContent = String(this.packSize);
    });
    const proteinPct = this.packSize > 0 ? Math.min(100, (protein / this.packSize) * 100) : 0;
    this.els.proteinProgress.forEach((el) => {
      el.style.width = `${proteinPct}%`;
    });

    // --- per-group progress (carbs) ---
    this.els.carbFilled.forEach((el) => {
      el.textContent = String(carb);
    });
    this.els.carbTarget.forEach((el) => {
      el.textContent = String(this.packSize);
    });
    const carbPct = this.packSize > 0 ? Math.min(100, (carb / this.packSize) * 100) : 0;
    this.els.carbProgress.forEach((el) => {
      el.style.width = `${carbPct}%`;
    });

    // --- per-group progress (vegetables) ---
    this.els.vegFilled.forEach((el) => {
      el.textContent = String(veg);
    });
    this.els.vegTarget.forEach((el) => {
      el.textContent = String(this.packSize);
    });
    const vegPct = this.packSize > 0 ? Math.min(100, (veg / this.packSize) * 100) : 0;
    this.els.vegProgress.forEach((el) => {
      el.style.width = `${vegPct}%`;
    });

    // Highlight whichever grid is next in the Protein → Carbs → Vegetables
    // order, so it's obvious what to do next instead of relying on the
    // shopper to notice a number changing somewhere else on the page.
    if (this.els.carbGroup) {
      this.els.carbGroup.classList.toggle('is-next', proteinDone && !carbDone);
    }
    if (this.els.vegGroup) {
      this.els.vegGroup.classList.toggle('is-next', proteinDone && carbDone && !vegDone);
    }

    this.#renderHint(proteinDone, carbDone, vegDone);
    this.#renderList();

    const { symbol, amount } = this.#money(this.#subtotal());
    this.els.total.forEach((el) => {
      el.innerHTML = '';
      if (symbol) {
        const s = document.createElement('span');
        s.className = 'dm-b__cur';
        s.textContent = symbol;
        el.appendChild(s);
      }
      const a = document.createElement('span');
      a.textContent = amount;
      el.appendChild(a);
    });

    // Ready only once ALL THREE groups are filled to exactly the chosen
    // pack size.
    const ready = proteinDone && carbDone && vegDone;
    let label;
    if (ready) {
      label = this.strings.ready || 'Add to cart';
    } else if (!proteinDone) {
      const remaining = this.packSize - protein;
      label = (this.strings.needProtein || 'Add [n] more protein').replace('[n]', String(remaining));
    } else if (!carbDone) {
      const remaining = this.packSize - carb;
      label = (this.strings.needCarb || 'Add [n] more carbs').replace('[n]', String(remaining));
    } else {
      const remaining = this.packSize - veg;
      label = (this.strings.needVeg || 'Add [n] more veg').replace('[n]', String(remaining));
    }

    this.els.submit.forEach((el) => {
      el.disabled = !ready;
      if (!el.hasAttribute('data-dm-static-label')) el.textContent = label;
    });

    if (this.els.live) {
      this.els.live.textContent = (this.strings.progress || '[n] items, [total]')
        .replace('[n]', String(total))
        .replace('[total]', `${symbol}${amount}`);
    }
  }

  /* Plain-language "what do I do next" banner — the whole point is that a
     shopper never has to work out for themselves which of three grids
     still needs items. */
  #renderHint(proteinDone, carbDone, vegDone) {
    const { protein, carb, veg } = this.#counts();
    let text;
    let done = false;
    if (!proteinDone) {
      text = (this.strings.hintProtein || 'Choose your protein: [n] left').replace(
        '[n]',
        String(this.packSize - protein),
      );
    } else if (!carbDone) {
      text = (this.strings.hintCarb || 'Protein done. Now choose your carbs ([n] left)').replace(
        '[n]',
        String(this.packSize - carb),
      );
    } else if (!vegDone) {
      text = (this.strings.hintVeg || 'Now choose your vegetables ([n] left)').replace(
        '[n]',
        String(this.packSize - veg),
      );
    } else {
      text = this.strings.hintReady || 'Your plate is ready. Add it to cart.';
      done = true;
    }
    this.els.hint.forEach((el) => {
      el.textContent = text;
      el.classList.toggle('is-done', done);
    });
  }

  #renderList() {
    const { list, empty } = this.els;
    if (!list) return;

    list.querySelectorAll('[data-dm-row]').forEach((r) => r.remove());

    if (this.selection.size === 0) {
      if (empty) empty.hidden = false;
      return;
    }
    if (empty) empty.hidden = true;

    this.selection.forEach((qty, id) => {
      const row = this.#rowFor(id);
      if (!row) return;

      const wrap = document.createElement('div');
      wrap.className = 'dm-b__side-row';
      wrap.setAttribute('data-dm-row', '');

      const name = document.createElement('span');
      name.className = 'dm-b__side-row-name';
      name.textContent = row.querySelector('.dm-b__name')?.textContent?.trim() || '';

      const q = document.createElement('span');
      q.className = 'dm-b__side-row-qty';
      q.textContent = `×${qty}`;

      wrap.append(name, q);
      list.appendChild(wrap);
    });
  }

  async #submit() {
    const { protein, carb, veg } = this.#counts();
    const hasVeg = !this.goal || this.goal.hasVeg !== false;
    if (protein < this.packSize || carb < this.packSize || (hasVeg && veg < this.packSize)) return;

    const { submit, error } = this.els;
    error.forEach((el) => {
      el.textContent = '';
    });
    submit.forEach((el) => {
      el.disabled = true;
    });

    const plateId = `cp-${this.goal ? this.goal.tag : 'plate'}-${Date.now()}`;
    const plateLabel = `${this.strings.plateProperty || 'Custom Prep plate'}${
      this.goal && this.goal.label ? ` (${this.goal.label})` : ''
    }`;
    // Protein and carbs must each hit the chosen pack size exactly, plus
    // vegetables too for a goal that tracks them separately — so the true
    // required total is packSize x2 or x3 depending on the goal.
    const groupCount = this.goal && this.goal.hasVeg === false ? 2 : 3;

    const items = [];
    this.selection.forEach((qty, id) => {
      const row = this.#rowFor(id);
      items.push({
        id: Number(id),
        quantity: qty,
        properties: {
          _dm_plate_id: plateId,
          _dm_plate: plateLabel,
          _dm_min: String(this.packSize * groupCount),
          /* How many real servings ONE unit of this line represents — 1
             for a protein pack, 5 for a "pack of 5" carb/veg add-on. The
             cart-wide minimum check (dm-cart-minimums.liquid) only ever
             sees Shopify's raw line quantity, so without this a plate at
             the true required 30 servings (10+10+10) looks like just 14
             lines (10 protein + 2 rice packs + 2 veg packs) and gets
             wrongly blocked short by 16. */
          _dm_pack_qty: String(row ? this.#packQty(row) : 1),
        },
      });
    });

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.description || payload.message || 'add_failed');
      }

      // Always reset the builder — the pack now lives in the cart.
      this.selection.clear();
      this.#render();
      this.dispatchEvent(new CustomEvent('dm:plate-added', { bubbles: true, detail: { plateId } }));

      // Prefer the drawer: it shows progress toward the order minimum and
      // the free-delivery threshold, which a redirect to /cart would not.
      if (document.querySelector('dm-cart-drawer')) {
        document.dispatchEvent(new CustomEvent('dm:cart-added'));
      } else if (this.dataset.cartRedirect !== 'stay') {
        window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
      }
    } catch (err) {
      const message =
        err.message && err.message !== 'add_failed'
          ? err.message
          : this.strings.genericError || 'Sorry, we could not add this plate to your cart.';
      error.forEach((el) => {
        el.textContent = message;
      });
      submit.forEach((el) => {
        el.disabled = false;
      });
    }
  }
}

if (!customElements.get('dm-custom-prep')) {
  customElements.define('dm-custom-prep', DMCustomPrep);
}
